<script setup lang="ts">
import { ref, watch } from 'vue'
import { X, GraduationCap } from 'lucide-vue-next'
import LoginForm from './LoginForm.vue'
import SignupForm from './SignupForm.vue'
import ForgotPasswordForm from './ForgotPasswordForm.vue'

type AuthView = 'login' | 'signup' | 'forgot-password'

interface Props {
  show: boolean
  initialView?: AuthView
  title?: string
  subtitle?: string
  showTutorBanner?: boolean
  closeable?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  show: false,
  initialView: 'login',
  title: '',
  subtitle: '',
  showTutorBanner: true,
  closeable: true,
})

const emit = defineEmits<{
  (e: 'close'): void
  (e: 'success'): void
}>()

const currentView = ref<AuthView>(props.initialView)

// Reset view when modal opens
watch(() => props.show, (newVal) => {
  if (newVal) {
    currentView.value = props.initialView
  }
})

function switchTo(view: AuthView) {
  currentView.value = view
}

function handleSuccess() {
  emit('success')
}

function closeModal() {
  if (props.closeable) {
    emit('close')
  }
}

// Close on escape key (only if closeable)
function handleKeydown(e: KeyboardEvent) {
  if (e.key === 'Escape' && props.closeable) {
    closeModal()
  }
}
</script>

<template>
  <Teleport to="body">
    <Transition
      enter-active-class="transition ease-out duration-200"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition ease-in duration-150"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div
        v-if="show"
        class="fixed inset-0 z-50 overflow-y-auto"
        @keydown="handleKeydown"
      >
        <!-- Backdrop -->
        <div
          class="fixed inset-0 bg-black/50 backdrop-blur-sm"
          @click="closeable && closeModal()"
        />

        <!-- Modal Container -->
        <div class="flex min-h-full items-center justify-center p-4">
          <Transition
            enter-active-class="transition ease-out duration-200"
            enter-from-class="opacity-0 scale-95"
            enter-to-class="opacity-100 scale-100"
            leave-active-class="transition ease-in duration-150"
            leave-from-class="opacity-100 scale-100"
            leave-to-class="opacity-0 scale-95"
          >
            <div
              v-if="show"
              class="relative w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden"
              @click.stop
            >
              <!-- Close Button (only if closeable) -->
              <button
                v-if="closeable"
                @click="closeModal"
                class="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors z-10"
              >
                <X class="w-5 h-5" />
              </button>

              <!-- Logo -->
              <div class="pt-8 pb-2 flex justify-center">
                <div class="w-12 h-12 bg-gradient-to-br from-blue-600 to-purple-600 rounded-xl flex items-center justify-center">
                  <GraduationCap class="w-6 h-6 text-white" />
                </div>
              </div>

              <!-- Custom Title/Subtitle if provided -->
              <div v-if="title || subtitle" class="px-8 text-center mb-2">
                <p v-if="title" class="text-sm font-medium text-blue-600 bg-blue-50 inline-block px-3 py-1 rounded-full">
                  {{ title }}
                </p>
                <p v-if="subtitle" class="text-gray-500 text-sm mt-2">
                  {{ subtitle }}
                </p>
              </div>

              <!-- Content -->
              <div class="px-8 pb-8">
                <Transition
                  mode="out-in"
                  enter-active-class="transition ease-out duration-150"
                  enter-from-class="opacity-0 translate-x-4"
                  enter-to-class="opacity-100 translate-x-0"
                  leave-active-class="transition ease-in duration-100"
                  leave-from-class="opacity-100 translate-x-0"
                  leave-to-class="opacity-0 -translate-x-4"
                >
                  <LoginForm
                    v-if="currentView === 'login'"
                    key="login"
                    @success="handleSuccess"
                    @switch-to="switchTo"
                  />
                  <SignupForm
                    v-else-if="currentView === 'signup'"
                    key="signup"
                    :show-tutor-banner="showTutorBanner"
                    @success="handleSuccess"
                    @switch-to="switchTo"
                  />
                  <ForgotPasswordForm
                    v-else-if="currentView === 'forgot-password'"
                    key="forgot-password"
                    @success="handleSuccess"
                    @switch-to="switchTo"
                  />
                </Transition>
              </div>
            </div>
          </Transition>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>
